"use client"

import { useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { type PropertyModels, propertyApi } from "@/api/client/property"
import { getErrorMessage } from "@/api/instance"
import {
  PropertyForm,
  type PropertyFormSubmitValues,
  type PropertyFormValues,
} from "@/components/properties/PropertyForm"
import { PropertyHeader } from "@/components/properties/PropertyHeader"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog/dialog"
import { Loading } from "@/components/ui/loading"

export function PropertyEditPage() {
  const { propertyId: propertyIdParam } = useParams()
  const propertyId = propertyIdParam ? Number(propertyIdParam) : undefined
  const navigate = useNavigate()
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  // 取得
  const {
    data: propertyResponse,
    isLoading,
    error,
    refetch,
  } = propertyApi.usePropertyGet(propertyId, undefined, {
    query: {
      enabled: propertyId !== undefined,
    },
  })

  // 更新
  const propertyUpdateMutation = propertyApi.usePropertyUpdate({
    mutation: {
      onSuccess: async () => {
        toast.success("物件情報を更新しました")
        await refetch()

        // ページトップにスクロール
        window.scrollTo(0, 0)

        // 遷移元によって遷移先を変更
        const from = (location.state as { from?: string })?.from
        if (from === "list") {
          // 物件一覧から来た場合は物件一覧に戻る
          navigate("/properties", { state: { shouldRefetch: true } })
        } else if (propertyId) {
          // 物件詳細から来た場合は物件詳細に戻る
          navigate(`/properties/${propertyId}`, { state: { shouldRefetch: true } })
        } else {
          navigate("/properties", { state: { shouldRefetch: true } })
        }
      },
    },
  })

  // 削除
  const propertyDeleteMutation = propertyApi.usePropertyDelete({
    mutation: {
      onSuccess: () => {
        toast.success("物件を削除しました")
        navigate("/properties", { state: { shouldRefetch: true } })
      },
      onError: (error) => {
        toast.error("物件の削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmit = async (values: PropertyFormSubmitValues) => {
    if (propertyId === undefined) return

    setIsSubmitting(true)
    try {
      await propertyUpdateMutation.mutateAsync({
        id: propertyId,
        data: values as unknown as PropertyModels.PropertyUpdateBody,
      })
    } catch (error) {
      console.error("Error updating property:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    // ページトップにスクロール
    window.scrollTo(0, 0)

    // 遷移元によって遷移先を変更
    const from = (location.state as { from?: string })?.from
    if (from === "list") {
      // 物件一覧から来た場合は物件一覧に戻る
      navigate("/properties")
    } else if (propertyId) {
      // 物件詳細から来た場合は物件詳細に戻る
      navigate(`/properties/${propertyId}`)
    } else {
      navigate("/properties")
    }
  }

  const handleDelete = async () => {
    if (propertyId === undefined) return

    setIsDeleting(true)
    try {
      await propertyDeleteMutation.mutateAsync({
        id: propertyId,
      })
      setOpenDeleteDialog(false)
    } catch (error) {
      console.error("Error deleting property:", error)
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return <Loading className="mt-10" size="lg" text="物件情報を読み込んでいます..." />
  }

  if (error || !propertyResponse) {
    interface ErrorWithDetails extends Error {
      status?: number
      type?: string
    }
    const errorData = error as ErrorWithDetails
    const is404 = errorData?.status === 404 || errorData?.type === "/not-found"
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">
          {is404 ? "指定された物件が見つかりませんでした。" : "物件情報の取得に失敗しました。"}
        </div>
        <Button onClick={() => navigate("/properties")} variant="outline">
          物件一覧に戻る
        </Button>
      </div>
    )
  }

  const propertyData: PropertyFormValues = {
    name: propertyResponse.property.name,
    nameKana: propertyResponse.property.nameKana,
    upperZip: propertyResponse.property.upperZip,
    lowerZip: propertyResponse.property.lowerZip,
    prefecture: propertyResponse.property.prefecture,
    prefectureKana: propertyResponse.property.prefectureKana,
    city: propertyResponse.property.city,
    cityKana: propertyResponse.property.cityKana,
    town: propertyResponse.property.town,
    townKana: propertyResponse.property.townKana,
    district: propertyResponse.property.district,
    districtKana: propertyResponse.property.districtKana,
    structure: propertyResponse.property.structure,
    type: propertyResponse.property.type,
    builtYear: propertyResponse.property.builtYear,
    floor: String(propertyResponse.property.floor),
    ownershipType: propertyResponse.property.ownershipType,
    managementCompanyName: propertyResponse.property.managementCompanyName,
    managementCompanyPhone: propertyResponse.property.managementCompanyPhone,
    note: propertyResponse.property.note,
  }

  if (!propertyId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-slate-600">物件IDが不正です。</div>
        <Button onClick={() => navigate("/properties")} variant="outline">
          物件一覧に戻る
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="mb-6">
        <PropertyHeader propertyId={propertyId} onBack={handleCancel} />
      </div>

      <div className="space-y-6">
        <PropertyForm
          defaultValues={propertyData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onDelete={() => setOpenDeleteDialog(true)}
          isEdit
          isSubmitting={isSubmitting || propertyUpdateMutation.isPending}
        />

        <div className="flex justify-between items-center">
          <Dialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>物件を削除しますか？</DialogTitle>
                <div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    この操作は取り消すことができません。物件に関連するすべてのデータが削除されます。
                  </p>
                </div>
              </DialogHeader>
              <DialogFooter className="flex flex-col gap-3 justify-between">
                <DialogTrigger asChild>
                  <Button variant="outline">キャンセル</Button>
                </DialogTrigger>
                <Button onClick={handleDelete} variant="destructive" disabled={isDeleting}>
                  {isDeleting ? "削除中..." : "削除する"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </>
  )
}
